import { useContext, useRef } from 'react';
import { useQuery } from 'react-query';
import { getFormOptionslist } from '../../api/studiesAPI';
import useInput from '../../hooks/use-input';
import EditStudyContext from '../../store/edit-study-context';
import Select from 'react-select';
import Button from '../ui/Button';
import Loader from '../ui/Loader';

import './PreliminaryStudyDataForm.scss';

const PreliminaryStudyDataForm = ({
	title,
	onDocsSelection,
	onCancel,
	informedConsentEntered
}) => {
	const editStudyCtx = useContext(EditStudyContext);
	const {
		value: enteredTitle,
		isValid: enteredTitleIsValid,
		hasError: titleInputHasError,
		valueChangeHandler: titleChangedHandler,
		inputBlurHandler: titleBlurHandler,
		reset: resetTitleInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.title
	);

	const {
		value: enteredLongTitle,
		isValid: enteredLongTitleIsValid,
		hasError: longTitleInputHasError,
		valueChangeHandler: longTitleChangedHandler,
		inputBlurHandler: longTitleBlurHandler,
		reset: resetLongTitleInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.longTitle
	);

	const {
		value: enteredVersion,
		isValid: enteredVersionIsValid,
		hasError: versionInputHasError,
		valueChangeHandler: versionChangedHandler,
		inputBlurHandler: versionBlurHandler,
		reset: resetVersionInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.version
	);

	const {
		value: enteredProtocolDate,
		isValid: enteredProtocolDateIsValid,
		hasError: protocolDateInputHasError,
		valueChangeHandler: protocolDateChangedHandler,
		inputBlurHandler: protocolDateBlurHandler,
		reset: resetProtocolDateInput
	} = useInput(
		(value) => !isNaN(new Date(value)),
		editStudyCtx.formValues.protocolDate
	);

	const {
		value: enteredPi,
		isValid: enteredPiIsValid,
		hasError: piInputHasError,
		valueChangeHandler: piChangedHandler,
		inputBlurHandler: piBlurHandler,
		reset: resetPiInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.pi
	);

	const {
		value: enteredDeparment,
		isValid: enteredDeparmentIsValid,
		hasError: deparmentInputHasError,
		valueChangeHandler: deparmentChangedHandler,
		inputBlurHandler: deparmentBlurHandler,
		reset: resetDeparmentInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.deparment
	);

	const {
		value: enteredSponsor,
		isValid: enteredSponsorIsValid,
		hasError: sponsorInputHasError,
		valueChangeHandler: sponsorChangedHandler,
		inputBlurHandler: sponsorBlurHandler,
		reset: resetSponsorInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.sponsor
	);

	const {
		value: enteredFundingType,
		isValid: enteredFundingTypeIsValid,
		hasError: fundingTypeInputHasError,
		valueChangeHandler: fundingTypeChangedHandler,
		inputBlurHandler: fundingTypeBlurHandler,
		reset: resetFundingTypeInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.fundingType
	);

	const {
		value: enteredPhaseOfStudy,
		isValid: enteredPhaseOfStudyIsValid,
		hasError: phaseOfStudyInputHasError,
		valueChangeHandler: phaseOfStudyChangedHandler,
		inputBlurHandler: phaseOfStudyBlurHandler,
		reset: resetPhaseOfStudyInput
	} = useInput(
		(value) => value.length > 0,
		editStudyCtx.formValues.phaseOfStudy
	);

	const {
		value: enteredClinicalTrial,
		isValid: enteredClinicalTrialIsValid,
		hasError: clinicalTrialInputHasError,
		valueChangeHandler: clinicalTrialChangedHandler,
		inputBlurHandler: clinicalTrialBlurHandler,
		reset: resetClinicalTrialInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.nationalClinicalTrial
	);

	const {
		value: enteredInformedConsentFormDate,
		isValid: enteredInformedConsentFormDateIsValid,
		hasError: informedConsentFormDateInputHasError,
		valueChangeHandler:
			informedConsentFormDateChangedHandler,
		inputBlurHandler: informedConsentFormDateBlurHandler,
		reset: resetInformedConsentFormDateInput
	} = useInput(
		(value) => value,
		editStudyCtx.formValues.informedConsentFormDate
	);

	const {
		value: enteredIrbApprobalDate,
		isValid: enteredIrbApprobalDateIsValid,
		hasError: irbApprobalDateInputHasError,
		valueChangeHandler: irbApprobalDateChangedHandler,
		inputBlurHandler: irbApprobalDateBlurHandler,
		reset: resetIrbApprobalDateInput
	} = useInput(
		(value) => value,
		editStudyCtx.formValues.irbApprobalDate
	);

	const {
		value: enteredCrfoBudgetReviewCompletedDate,
		isValid: enteredCrfoBudgetReviewCompletedDateIsValid,
		hasError: crfoBudgetReviewCompletedDateInputHasError,
		valueChangeHandler:
			crfoBudgetReviewCompletedDateChangedHandler,
		inputBlurHandler:
			crfoBudgetReviewCompletedDateDateBlurHandler,
		reset: resetCrfoBudgetReviewCompletedDateInput
	} = useInput(
		(value) => value,
		editStudyCtx.formValues.crfoBudgetReviewCompletedDate
	);

	const {
		value: enteredInvestigationalDrug,
		isValid: enteredInvestigationalDrugIsValid,
		hasError: investigationalDrugInputHasError,
		valueChangeHandler: investigationalDrugChangedHandler,
		inputBlurHandler: investigationalDrugBlurHandler,
		reset: resetInvestigationalDrugInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.nameOfInvestigationalDrug
	);

	const {
		value: enteredInvestigationalDevice,
		isValid: enteredInvestigationalDeviceIsValid,
		hasError: investigationalDeviceInputHasError,
		valueChangeHandler: investigationalDeviceChangedHandler,
		inputBlurHandler: investigationalDeviceBlurHandler,
		reset: resetInvestigationalDeviceInput
	} = useInput(
		(value) => value.trim() !== '',
		editStudyCtx.formValues.nameOfInvestigationalDevice
	);

	const clinicalTrialAgreementVerionRef = useRef('');
	const investigationalDrugRef = useRef('');
	const indRef = useRef('');
	const investigationalDeviceRef = useRef('');
	const ideRef = useRef('');

	const { isLoading, isError, error, data } = useQuery({
		queryKey: ['form-options'],
		queryFn: getFormOptionslist,
		retry: false,
		staleTime: 3000000 //5 minutes to refetch
	});

	if (isLoading) {
		return <Loader />;
	}

	const { piList, phasesOfStudy, fundingTypes } = data.data;

	const phaseOfStudyOptions = phasesOfStudy.map((phase) => {
		return {
			value: phase,
			label: phase.replaceAll('_', ' ')
		};
	});

	const updateTitle = (e) => {
		const newTitle = e.target.value;
		titleChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			title: newTitle
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateLongTitle = (e) => {
		const newLongTitle = e.target.value;
		longTitleChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			longTitle: newLongTitle
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateProtocolVersion = (e) => {
		const newProtocolVersion = e.target.value;
		versionChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			version: newProtocolVersion
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateProtocolDate = (e) => {
		const newProtocolDate = e.target.value;
		protocolDateChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			protocolDate: newProtocolDate
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updatePIname = (e) => {
		const newPIname = e.target.value;
		piChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			pi: newPIname
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateDeparment = (e) => {
		const newDeparment = e.target.value;
		deparmentChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			deparment: newDeparment
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateSponsor = (e) => {
		const newSponsor = e.target.value;
		sponsorChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			sponsor: newSponsor
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateFundingType = (e) => {
		const newFundingType = e.target.value;
		fundingTypeChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			fundingType: newFundingType
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updatePhaseOfStudy = (phases) => {
		const newPhasesOfStudy = phases.map(
			(phase) => phase.value
		);
		const newPhaseOfStudy = newPhasesOfStudy;
		phaseOfStudyChangedHandler(newPhasesOfStudy);
		const newFormValues = {
			...editStudyCtx.formValues,
			phaseOfStudy: newPhaseOfStudy
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateClinicalTrial = (e) => {
		const newClinicalTrial = e.target.value;
		clinicalTrialChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			nationalClinicalTrial: newClinicalTrial
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateInformedConsentFormDate = (e) => {
		const newInformedConsentFormDate = e.target.value;
		informedConsentFormDateChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			informedConsentFormDate: newInformedConsentFormDate
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateIrbApprobalDate = (e) => {
		const newIrbApprobalDate = e.target.value;
		irbApprobalDateChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			irbApprobalDate: newIrbApprobalDate
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateCrfoBudgetReviewCompletedDate = (e) => {
		const newCrfoBudgetReviewCompletedDate = e.target.value;
		crfoBudgetReviewCompletedDateChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			crfoBudgetReviewCompletedDate:
				newCrfoBudgetReviewCompletedDate
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateClinicalTrialAgreementVerion = (e) => {
		const newClinicalTrialAgreementVerion = e.target.value;
		const newFormValues = {
			...editStudyCtx.formValues,
			clinicalTrialAgreementVersion:
				newClinicalTrialAgreementVerion
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateInvestigationalDrug = (e) => {
		const newInvestigationalDrug = e.target.value;
		investigationalDrugChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			nameOfInvestigationalDrug: newInvestigationalDrug
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateInvestigationalDevice = (e) => {
		const newInvestigationalDevice = e.target.value;
		investigationalDeviceChangedHandler(e);
		const newFormValues = {
			...editStudyCtx.formValues,
			nameOfInvestigationalDevice: newInvestigationalDevice
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateInd = (e) => {
		const newInd = e.target.value;
		const newFormValues = {
			...editStudyCtx.formValues,
			IND: newInd
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	const updateIde = (e) => {
		const newIde = e.target.value;
		const newFormValues = {
			...editStudyCtx.formValues,
			IDE: newIde
		};
		editStudyCtx.updateFormValues(newFormValues);
	};

	let formIsValid = false;
	if (
		enteredTitleIsValid &&
		enteredLongTitleIsValid &&
		enteredVersionIsValid &&
		enteredProtocolDateIsValid &&
		enteredPiIsValid &&
		enteredDeparmentIsValid &&
		enteredSponsorIsValid &&
		enteredFundingTypeIsValid &&
		enteredPhaseOfStudyIsValid &&
		enteredClinicalTrialIsValid &&
		enteredInvestigationalDrugIsValid &&
		enteredInvestigationalDeviceIsValid
	) {
		formIsValid = true;
	}

	const gatherFormData = (event) => {
		event.preventDefault();

		let indNumber = indRef.current.value;
		let ideNumber = ideRef.current.value;

		if (isNaN(indNumber)) {
			indNumber = 'N/A';
		}
		if (isNaN(ideNumber)) {
			ideNumber = 'N/A';
		}

		const formDataResult = {
			title: enteredTitle,
			version: enteredVersion,
			pi: enteredPi,
			deparment: enteredDeparment,
			sponsor: enteredSponsor,
			fundingType: enteredFundingType,
			phaseOfStudy: enteredPhaseOfStudy,
			nationalClinicalTrial: enteredClinicalTrial,
			informedConsentFormDate:
				enteredInformedConsentFormDate,
			irbApprobalDate: enteredIrbApprobalDate,
			clinicalTrialAgreementVersion:
				clinicalTrialAgreementVerionRef.current.value,
			crfoBudgetReviewCompletedDate:
				enteredCrfoBudgetReviewCompletedDate,
			nameOfInvestigationalDrug: enteredInvestigationalDrug,
			nameOfInvestigationalDevice:
				enteredInvestigationalDevice,
			IDN: indNumber,
			IDE: ideNumber,
			longTitle: enteredLongTitle,
			protocolDate: enteredProtocolDate
		};
		if (enteredInformedConsentFormDate) {
			informedConsentEntered(true);
		}
		onDocsSelection(formDataResult);
		resetTitleInput();
		resetLongTitleInput();
		resetVersionInput();
		resetProtocolDateInput();
		resetPiInput();
		resetDeparmentInput();
		resetSponsorInput();
		resetFundingTypeInput();
		resetPhaseOfStudyInput();
		resetClinicalTrialInput();
		resetInformedConsentFormDateInput();
		resetIrbApprobalDateInput();
		resetCrfoBudgetReviewCompletedDateInput();
		resetInvestigationalDrugInput();
		resetInvestigationalDeviceInput();
	};

	const titleInputClasses = titleInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const longTitleInputClasses = longTitleInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const versionInputClasses = versionInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const protocolDateClasses = protocolDateInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const piInputClasses = piInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const deparmentInputClasses = deparmentInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const sponsorInputClasses = sponsorInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const fundingTypeClasses = fundingTypeInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const phaseOfStudyClasses = phaseOfStudyInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const clinicalTrialClasses = clinicalTrialInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const invDrugClasses = investigationalDrugInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const invDeviceClasses =
		investigationalDeviceInputHasError
			? 'form-control is-invalid'
			: 'form-control';

	const informedConsentDateClasses = 'form-control';

	const irbApprobalDateClasses = 'form-control';

	const crfoBudgetReviewCompletedDateInputClasses =
		'form-control';

	return (
		<form className='add-data-form'>
			<div className='form-title'>
				<h5>{title} </h5>
			</div>
			<div className='form-subtitle'>
				<p>Please complete each input to add a new study</p>
				<p>
					<span className='mandatory-field'>*</span>{' '}
					Mandatory fields
				</p>
			</div>
			<div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label htmlFor='protocol-title'>
							Protocol Title (Short Name){' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							id='protocol-title'
							type='text'
							placeholder='Bone Cancer - Cell Study'
							defaultValue={editStudyCtx.formValues.title}
							onBlur={titleBlurHandler}
							className={titleInputClasses}
							onChange={updateTitle}
						/>
						{titleInputHasError && (
							<p className='error-text'>
								Name must not be empty.
							</p>
						)}
					</div>
					<div className='col-sm-6'>
						<label htmlFor='protocol-title-long'>
							Protocol Title (Long Name){' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							id='protocol-title-long'
							type='text'
							placeholder='Bone cancer - Treatment of Patients With…'
							defaultValue={
								editStudyCtx.formValues.longTitle
							}
							className={longTitleInputClasses}
							onBlur={longTitleBlurHandler}
							onChange={updateLongTitle}
						/>
						{longTitleInputHasError && (
							<p className='error-text'>
								Long name must not be empty.
							</p>
						)}
					</div>
				</div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label htmlFor='protocol-version'>
							Protocol Version{' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							id='protocol-version'
							placeholder='1.0'
							defaultValue={editStudyCtx.formValues.version}
							min='0'
							step='.1'
							type='number'
							className={versionInputClasses}
							onChange={updateProtocolVersion}
							onBlur={versionBlurHandler}
						/>
						{versionInputHasError && (
							<p className='error-text'>
								Version must not be empty.
							</p>
						)}
					</div>
					<div className='col-sm-6'>
						<label htmlFor='protocol-date'>
							Protocol date{' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							id='protocol-date'
							type='date'
							defaultValue={
								editStudyCtx.formValues.protocolDate
							}
							className={protocolDateClasses}
							onChange={updateProtocolDate}
							onBlur={protocolDateBlurHandler}
						/>
						{protocolDateInputHasError && (
							<p className='error-text'>
								Protocol date must not be empty.
							</p>
						)}
					</div>
				</div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label htmlFor='pi-names'>P.I. Name*</label>
						<select
							name='pi-names'
							id='pi-names'
							className={piInputClasses}
							onChange={updatePIname}
							onBlur={piBlurHandler}
							defaultValue={editStudyCtx.formValues.pi}
						>
							<option value=' '>Select a Name</option>
							{piList.map((pi) => {
								return (
									<option key={pi._id} value={pi._id}>
										{pi.firstname} {pi.lastname}
									</option>
								);
							})}
						</select>
						{piInputHasError && (
							<p className='error-text'>
								P.I must not be empty.
							</p>
						)}
					</div>
					<div className='col-sm-6'>
						<label htmlFor='deparment'>
							Department{' '}
							<span className='mandatory-field'>*</span>
						</label>
						<select
							name='deparment'
							id='deparment'
							className={deparmentInputClasses}
							onChange={updateDeparment}
							onBlur={deparmentBlurHandler}
							defaultValue={
								editStudyCtx.formValues.deparment
							}
						>
							<option
								value={editStudyCtx.formValues.deparment}
								disabled
								hidden
							>
								{editStudyCtx.formValues.deparment}
							</option>
							<option value='Cancer Center'>
								Cancer Center
							</option>
							<option value='Non-Cancer'>Non-Cancer</option>
						</select>
						{deparmentInputHasError && (
							<p className='error-text'>
								Deparment must not be empty.
							</p>
						)}
					</div>
				</div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label htmlFor='funding-sponsor'>
							Funding Sponsor{' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							id='funding-sponsor'
							type='text'
							defaultValue={editStudyCtx.formValues.sponsor}
							className={sponsorInputClasses}
							onChange={updateSponsor}
							onBlur={sponsorBlurHandler}
						/>
						{sponsorInputHasError && (
							<p className='error-text'>
								Sponsor must not be empty.
							</p>
						)}
					</div>
					<div className='col-sm-6'>
						<label htmlFor='funding-type'>
							Funding Type{' '}
							<span className='mandatory-field'>*</span>
						</label>
						<select
							name='funding'
							id='funding-type'
							className={fundingTypeClasses}
							onChange={updateFundingType}
							onBlur={fundingTypeBlurHandler}
							defaultValue={
								editStudyCtx.formValues.fundingType
							}
						>
							<option
								value={editStudyCtx.formValues.fundingType}
								disabled
								hidden
							>
								{editStudyCtx.formValues.fundingType}
							</option>
							{fundingTypes.map((fType, i) => {
								return (
									<option key={i} value={fType}>
										{fType}
									</option>
								);
							})}
						</select>
						{fundingTypeInputHasError && (
							<p className='error-text'>
								Funding Type must not be empty.
							</p>
						)}
					</div>
				</div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label htmlFor='phase-of-study'>
							Phase of Study{' '}
							<span className='mandatory-field'>*</span>
						</label>
						<Select
							isMulti
							defaultValue={
								editStudyCtx.formValues.phaseOfStudy
							}
							options={phaseOfStudyOptions}
							onChange={updatePhaseOfStudy}
						/>
					</div>
					<div className='col-sm-6'>
						<label htmlFor='national-clinical-trial'>
							National Clinical Trial{' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							id='national-clinical-trial'
							type='text'
							placeholder='NCT12345678'
							defaultValue={
								editStudyCtx.formValues
									.nationalClinicalTrial
							}
							className={clinicalTrialClasses}
							onChange={updateClinicalTrial}
							onBlur={clinicalTrialBlurHandler}
						/>
						{clinicalTrialInputHasError && (
							<p className='error-text'>
								National Clinical Trial number invalid.
							</p>
						)}
					</div>
				</div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label htmlFor='informed-consent-form-date'>
							Informed Consent Form Date
						</label>
						<input
							id='informed-consent-form-date'
							type='date'
							defaultValue={
								editStudyCtx.formValues
									.informedConsentFormDate
							}
							className={informedConsentDateClasses}
							onChange={updateInformedConsentFormDate}
							onBlur={informedConsentFormDateBlurHandler}
						/>
					</div>
					<div className='col-sm-6'>
						<label htmlFor='irb-approval-date'>
							IRB Review Date
						</label>
						<input
							id='irb-approval-date'
							type='date'
							defaultValue={
								editStudyCtx.formValues.irbApprobalDate
							}
							className={irbApprobalDateClasses}
							onChange={updateIrbApprobalDate}
							onBlur={irbApprobalDateBlurHandler}
						/>
					</div>
				</div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label htmlFor='clinical-trial-agreement-version'>
							Clinical Trial Agreement version
						</label>
						<input
							id='clinical-trial-agreement-version'
							type='text'
							className='form-control'
							defaultValue={
								editStudyCtx.formValues
									.clinicalTrialAgreementVersion
							}
							placeholder='N/A'
							onChange={updateClinicalTrialAgreementVerion}
							ref={clinicalTrialAgreementVerionRef}
						/>
					</div>
					<div className='col-sm-6'>
						<label htmlFor='crfo-budget-review-date'>
							Budget Review Approval Date
						</label>
						<input
							id='crfo-budget-review-date'
							type='date'
							defaultValue={
								editStudyCtx.formValues
									.crfoBudgetReviewCompletedDate
							}
							className={
								crfoBudgetReviewCompletedDateInputClasses
							}
							onChange={updateCrfoBudgetReviewCompletedDate}
							onBlur={
								crfoBudgetReviewCompletedDateDateBlurHandler
							}
						/>
					</div>
				</div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label htmlFor='investigational-drug'>
							Name of Investigational Drug{' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							id='investigational-drug'
							type='text'
							className={invDrugClasses}
							placeholder='N/A'
							defaultValue={
								editStudyCtx.formValues
									.nameOfInvestigationalDrug
							}
							onChange={updateInvestigationalDrug}
							onBlur={investigationalDrugBlurHandler}
						/>
						{investigationalDrugInputHasError && (
							<p className='error-text'>
								Name of Investigational Drug must not be
								empty.
							</p>
						)}
					</div>
					<div className='col-sm-6'>
						<label htmlFor='ind'>IND#</label>
						<input
							id='ind'
							type='text'
							className='form-control'
							placeholder='N/A'
							defaultValue={editStudyCtx.formValues.IND}
							onChange={updateInd}
							ref={indRef}
						/>
					</div>
				</div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label htmlFor='investigational-device'>
							Name of Investigational Device{' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							id='investigational-device'
							type='text'
							className={invDeviceClasses}
							placeholder='N/A'
							defaultValue={
								editStudyCtx.formValues
									.nameOfInvestigationalDevice
							}
							onChange={updateInvestigationalDevice}
							onBlur={investigationalDeviceBlurHandler}
						/>
						{investigationalDeviceInputHasError && (
							<p className='error-text'>
								Name of Investigational Drug must not be
								empty.
							</p>
						)}
					</div>
					<div className='col-sm-6'>
						<label htmlFor='ide'>IDE#</label>
						<input
							id='ide'
							type='text'
							className='form-control'
							placeholder='N/A'
							defaultValue={editStudyCtx.formValues.IDE}
							onChange={updateIde}
							ref={ideRef}
						/>
					</div>
				</div>
			</div>
			<div className='text-center form-cta-container add-data-form-controllers'>
				<Button
					onClick={gatherFormData}
					className='primary-button'
					disabled={!formIsValid}
				>
					Upload Documents
				</Button>
				<Button
					onClick={onCancel}
					className='secondary-button'
				>
					Cancel
				</Button>
			</div>
		</form>
	);
};

export default PreliminaryStudyDataForm;
